import React, { useEffect, useState } from "react";
import axios from "axios";
import { Toaster, toast } from "react-hot-toast";
import { GrFormClose } from "react-icons/gr";
import { AiOutlinePlus } from "react-icons/ai";
import { BsCheckCircleFill, BsCircle } from "react-icons/bs";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Step,
  StepLabel,
  Stepper,
} from "@mui/material";
import { useFetchAllAppUserTypeQuery } from "../../../features/admin/AppUserType/appUserTypeApi";
import { useUpdateVendorMutation } from "../../../features/admin/vendor/vendorApi";

const WIZARD_STEPS = [
  "User types",
  "KYC documents",
  "API options",
  "Match rules",
  // "Override",
];

const groupDisplayTitle = (group) => {
  if (group?.user_types?.length) {
    return group.user_types.map((u) => u.charAt(0).toUpperCase() + u.slice(1)).join(", ");
  }
  return "New configuration";
};

const DOC_TO_API = { Aadhar: "aadhaar", PAN: "pan", GSTIN: "gstin" };

const RULE_NEST_META = {
  aadhaarName: { reg: "aadhaar", field: "name", slug: "aadhaar-name", error: "Aadhaar name must match the registered name", success: "Aadhaar name matched" },
  aadhaarDob: { reg: "aadhaar", field: "dob", slug: "aadhaar-dob", error: "User must be 18+ as per Aadhaar DOB", success: "Aadhaar DOB is valid" },
  aadhaarLast4: { reg: "aadhaar", field: "aadhaar_number", slug: "aadhaar-number", error: "Aadhaar last 4 digits do not match", success: "Aadhaar last 4 digits matched" },
  aadhaarPan: { reg: "aadhaar", field: "aadhaarPan", slug: "aadhaar-pan", error: "Aadhaar name must match the PAN name", success: "Aadhaar and PAN name matched" },
  panName: { reg: "pan", field: "name", slug: "pan-name", error: "PAN name must match the registered name", success: "PAN name matched" },
  panNumber: { reg: "pan", field: "pan_number", slug: "pan-number", error: "Invalid PAN number", success: "PAN number matched" },
  panAadhaar: { reg: "pan", field: "panAadhaar", slug: "pan-aadhaar", error: "PAN name must match the Aadhaar name", success: "PAN and Aadhaar Name matched" },
  panAadhaarImage: { reg: "pan", field: "panAadhaarImage", slug: "pan-aadhaar-image", error: "Your PAN photo does not match your Face picture", success: "PAN and Aadhaar Face matched" },
  panLinkedWithAadhaar: { reg: "pan", field: "panLinkedWithAadhaar", slug: "aadhaar-linked-with-pan", error: "PAN is not Linked with Aadhaar", success: "Aadhaar and PAN are linked" },
  gstShopName: { reg: "gstin", field: "shop_name", slug: "gst-shop_name", error: "Firm/Shop Name must match", success: "Firm/Shop Name matched" },
  upiName: { reg: "upi", field: "name", slug: "upi-name", error: "UPI name must match the registered name", success: "UPI name matched" },
  bankName: { reg: "bank", field: "name", slug: "bank-name", error: "Bank name must match the registered name", success: "Bank name matched" },
};

const matchRulesValidations = [
  { pause: "PAN" },
  { name: "Profile Name match with Pan", deny: true, threshold: 50, value: "panName" },
  { name: "Validate PAN Number", deny: true, value: "panNumber" },
  { name: "Match Pan Name = Aadhaar Name", deny: true, threshold: 50, value: "panAadhaar" },
  { name: "Use Aadhaar Face Image For Pan Face Verification", value: "panAadhaarImage" },
  { name: "PAN Linked with Aadhaar", deny: true, value: "panLinkedWithAadhaar" },
  { pause: "AADHAAR" },
  { name: "Profile Name match with Aadhaar", deny: true, threshold: 50, value: "aadhaarName" },
  { name: "DOB Year match with Aadhaar (Above 18)", deny: true, value: "aadhaarDob" },
  { name: "Validate Aadhar Number (Last 4 digits)", deny: true, value: "aadhaarLast4" },
  { name: "Match Aadhaar Name = Pan Name", deny: true, threshold: 50, value: "aadhaarPan" },
  { pause: "GSTIN" },
  { name: "Firm Name match with GSTIN", deny: true, threshold: 50, value: "gstShopName" },
  { pause: "BANKING" },
  {
    name: "Validate UPI",
    with_mobile: true,
    deny: true,
    threshold: 50,
    value: "upiName",
  },
  {
    name: "Validate Bank Details (With account holder name)",
    deny: true,
    threshold: 50,
    value: "bankName",
  },
];

const kycVerificationOptions = [
  { name: "Aadhaar", value: "aadhaar", options: ["online", "face", "default"] },
  { name: "PAN", value: "pan", options: ["online", "face", "default"] },
  { name: "GSTIN", value: "gstin", options: ["online", "default"] },
  { name: "UPI", value: "upi", options: ["online", "default"] },
  { name: "Bank", value: "bank", options: ["online", "default"] },
];

const KycOptionsObject = {
  Aadhar: {
    taken: false,
    users: [],
    regex: "/^[0-9]{12}$/",
  },
  PAN: {
    taken: false,
    users: [],
    regex: "/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/",
  },
  GSTIN: {
    taken: false,
    users: [],
    regex: "/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/",
  },
};

const defaultApiOptions = {
  aadhaar: "online",
  pan: "online",
  gstin: "online",
  upi: "online",
  bank: "online",
};

const KYC_DOC_KEYS = ["Aadhar", "PAN", "GSTIN"];

/** All regulations in kyc_dynamic_options (UPI/Bank live here, not in kyc_options) */
const REGULATION_KEYS = [
  { key: "aadhaar", label: "Aadhar", docKey: "Aadhar" },
  { key: "pan", label: "PAN", docKey: "PAN" },
  { key: "gstin", label: "GSTIN", docKey: "GSTIN" },
  { key: "upi", label: "UPI", docKey: null },
  { key: "bank", label: "Bank", docKey: null },
];

const DOC_MODE = { OFF: "off", REQUIRED: "required", OPTIONAL: "optional" };

/** DB stores kyc_dynamic_options as array (per user-type group) or single object */
export const parseKycDynamicOptions = (raw) => {
  if (!raw) return [];
  let parsed = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  return Array.isArray(parsed) ? parsed : [parsed];
};

export const flatMatchRulesFromConfig = (config) => {
  if (!config?.match_rules) return {};
  const acc = {};
  Object.values(config.match_rules).forEach((regulationRules) => {
    Object.values(regulationRules || {}).forEach((rule) => {
      if (rule?.ruleKey) {
        const { ruleKey, ...rest } = rule;
        acc[ruleKey] = rest;
      }
    });
  });
  return acc;
};

const apiDetailsFromConfig = (config) => ({
  aadhaar: config?.api_details?.aadhaar?.type || "online",
  pan: config?.api_details?.pan?.type || "online",
  gstin: config?.api_details?.gstin?.type || "online",
  upi: config?.api_details?.upi?.type || "online",
  bank: config?.api_details?.bank?.type || "online",
});

const isDocActive = (group, key) =>
  group.requiredDocs.includes(key) || group.optionalDocs.includes(key);

const getDocMode = (group, key) => {
  if (group.requiredDocs.includes(key)) return DOC_MODE.REQUIRED;
  if (group.optionalDocs.includes(key)) return DOC_MODE.OPTIONAL;
  return DOC_MODE.OFF;
};

const createConfigGroup = (partial = {}) => ({
  id: partial.id || `g-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  user_types: [...(partial.user_types || [])],
  requiredDocs: [...(partial.requiredDocs || [])],
  optionalDocs: [...(partial.optionalDocs || [])],
  apiDetails: { ...defaultApiOptions, ...(partial.apiDetails || {}) },
  matchRules: { ...(partial.matchRules || {}) },
  matchRulesNested: partial.matchRulesNested || null,
  // overrideOptions: partial.overrideOptions || {},
  // useOverrideOptions: Boolean(partial.useOverrideOptions),
});

const configGroupsFromDynamicOptions = (configs) => {
  if (!configs?.length) return [];
  return configs.map((cfg, i) => {
    const requiredDocs = cfg.valid_combinations || [];
    const optionalDocs = cfg.optional || [];
    const enabled = cfg.enabled_options || {};
    const legacyOptional = REGULATION_KEYS.filter(
      (r) => enabled[r.key] && !requiredDocs.includes(r.key) && !optionalDocs.includes(r.key),
    ).map((r) => r.key);
    return createConfigGroup({
      id: `loaded-${i}`,
      user_types: cfg.user_types || [],
      requiredDocs,
      optionalDocs: [...new Set([...optionalDocs, ...legacyOptional])],
      apiDetails: apiDetailsFromConfig(cfg),
      matchRules: flatMatchRulesFromConfig(cfg),
      matchRulesNested: cfg.match_rules || null,
      // overrideOptions: cfg.overrideOptions || {},
      // useOverrideOptions: Boolean(
      //   cfg.overrideOptions && Object.keys(cfg.overrideOptions).length > 0,
      // ),
    });
  });
};

export const nestFlatMatchRules = (flat) => {
  const match_rules = {};
  Object.entries(flat || {}).forEach(([ruleKey, opts]) => {
    const meta = RULE_NEST_META[ruleKey];
    if (!meta) return;
    const tpl = matchRulesValidations.find((r) => r.value === ruleKey);
    const fieldData = {
      name: meta.slug,
      ruleKey,
      error: meta.error,
      success: meta.success,
      ...(typeof tpl?.deny !== "undefined" ? { deny: opts.deny ?? tpl.deny } : {}),
      ...(typeof tpl?.threshold !== "undefined" ? { threshold: opts.threshold ?? tpl.threshold } : {}),
      ...(typeof tpl?.with_mobile !== "undefined" ? { with_mobile: opts.with_mobile ?? tpl.with_mobile } : {}),
    };
    if (!match_rules[meta.reg]) match_rules[meta.reg] = {};
    match_rules[meta.reg][meta.field] = fieldData;
  });
  return match_rules;
};

const buildEnabledOptions = (group) => ({
  aadhaar: isDocActive(group, "aadhaar"),
  pan: isDocActive(group, "pan"),
  gstin: isDocActive(group, "gstin"),
  upi: isDocActive(group, "upi"),
  bank: isDocActive(group, "bank"),
});

const buildApiDetailsPayload = (group) => {
  const out = {};
  REGULATION_KEYS.forEach(({ key }) => {
    if (isDocActive(group, key)) {
      out[key] = { type: group.apiDetails[key] || "online" };
    }
  });
  return out;
};

const buildKycDynamicOptionsFromGroups = (groups) =>
  groups
    .filter(
      (g) =>
        g.user_types.length > 0 &&
        (g.requiredDocs.length > 0 || g.optionalDocs.length > 0),
    )
    .map((g) => {
      const base = {
        optional: [...g.optionalDocs].sort(),
        user_types: [...g.user_types],
        valid_combinations: [...g.requiredDocs].sort(),
        enabled_options: buildEnabledOptions(g),
        api_details: buildApiDetailsPayload(g),
        match_rules: g.matchRulesNested || nestFlatMatchRules(g.matchRules),
        overrideOptions: {},
        // overrideOptions: g.useOverrideOptions ? g.overrideOptions || {} : {},
      };
      // if (g.useOverrideOptions && g.overrideOptions && Object.keys(g.overrideOptions).length) {
      //   return { ...base, ...g.overrideOptions, overrideOptions: g.overrideOptions };
      // }
      return base;
    });

const buildKycOptionsFromGroups = (groups) => {
  const kyc = JSON.parse(JSON.stringify(KycOptionsObject));
  Object.keys(kyc).forEach((doc) => {
    kyc[doc].users = [];
    kyc[doc].taken = false;
  });
  groups.forEach((g) => {
    const assignDoc = (apiKey) => {
      const docType = Object.keys(DOC_TO_API).find((k) => DOC_TO_API[k] === apiKey);
      if (!docType) return;
      g.user_types.forEach((ut) => {
        if (!kyc[docType].users.includes(ut)) kyc[docType].users.push(ut);
      });
      kyc[docType].taken = kyc[docType].users.length > 0;
    };
    [...g.requiredDocs, ...g.optionalDocs].forEach(assignDoc);
  });
  return kyc;
};

const groupSummaryLabel = (group) => ({
  required: group.requiredDocs.length ? group.requiredDocs.join(", ") : "—",
  optional: group.optionalDocs.length ? group.optionalDocs.join(", ") : "—",
  rules: Object.keys(group.matchRules).length,
});

const MatchRulesConfiguration = () => {
  const [vendorsList, setVendorsList] = useState([]);
  const [industryList, setIndustryList] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(0);
  const [selectedVendorData, setSelectedVendorData] = useState(null);
  const [selectedIndustryId, setSelectedIndustryId] = useState(0);
  const [selectedIndustryName, setSelectedIndustryName] = useState("");
  const [userTypes, setUserTypes] = useState([]);
  const [updateDisabled, setUpdateDisabled] = useState(true);

  const [configGroups, setConfigGroups] = useState([]);
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [configureModalOpen, setConfigureModalOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  // const [overrideDraft, setOverrideDraft] = useState("{}");

  const { data: allAppUserTypeData } = useFetchAllAppUserTypeQuery({
    token: sessionStorage.getItem("access_token"),
  });
  const [updateVendor, { data: updateVendorData, error: updateVendorError, isLoading: isSaving }] =
    useUpdateVendorMutation();

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BASE_URL}/admin/company`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("access_token")}` },
      })
      .then((res) => setVendorsList(res.data.body || []))
      .catch(console.log);

    axios
      .get(`${process.env.REACT_APP_BASE_URL}/admin/industry`, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("access_token")}`,
          "Content-Type": "application/json",
        },
      })
      .then((res) => setIndustryList(res.data.body))
      .catch(console.log);
  }, []);

  useEffect(() => {
    setUpdateDisabled(!(selectedIndustryId != 0 && selectedVendor != 0));
  }, [selectedIndustryId, selectedVendor]);

  useEffect(() => {
    if (updateVendorData) toast.success(updateVendorData.message || "Updated");
    if (updateVendorError) toast.error("Something went wrong");
  }, [updateVendorData, updateVendorError]);

  const editingGroup = configGroups.find((g) => g.id === editingGroupId) || null;

  const typesAssignedElsewhere = (groupId) =>
    configGroups.filter((g) => g.id !== groupId).flatMap((g) => g.user_types);

  useEffect(() => {
    if (!selectedVendorData) {
      setConfigGroups([]);
      setEditingGroupId(null);
      return;
    }
    const configs = parseKycDynamicOptions(selectedVendorData.kyc_dynamic_options);
    const types = selectedVendorData.user_types ? [...selectedVendorData.user_types] : [];
    setConfigGroups(configGroupsFromDynamicOptions(configs));
    setUserTypes(types);
    setEditingGroupId(null);
  }, [selectedVendorData]);

  const updateGroupInList = (groupId, patch) => {
    setConfigGroups((prev) =>
      prev.map((g) => (g.id === groupId ? { ...g, ...patch, matchRulesNested: null } : g)),
    );
  };

  const persistEditingGroup = () => {
    if (!editingGroup) return;
    updateGroupInList(editingGroup.id, { ...editingGroup });
  };

  const handleVendorChange = (e) => {
    const id = e.target.value;
    setSelectedVendor(id);
    if (id === "0" || id === 0) {
      setSelectedVendorData(null);
      setSelectedIndustryId(0);
      setSelectedIndustryName("");
      setUserTypes([]);
      return;
    }
    const vendor = vendorsList.find((v) => String(v.company_id) === String(id));
    if (!vendor) return;
    setSelectedVendorData(vendor);
    setSelectedIndustryId(vendor.industry_id || 0);
    setSelectedIndustryName(vendor.industry_name || "");
    if (vendor.user_types) setUserTypes([...vendor.user_types]);
  };

  const handleIndustryChange = (e) => {
    const id = e.target.value;
    setSelectedIndustryId(id);
    if (id === "0" || id === 0) {
      setSelectedIndustryName("");
      if (!selectedVendorData) setUserTypes([]);
      return;
    }
    const industry = industryList.find((i) => String(i.industry_id) === String(id));
    if (industry) {
      setSelectedIndustryName(industry.name);
      // Client selected → keep company user_types; industry list is metadata only
      if (!selectedVendorData && industry.users) {
        setUserTypes([...industry.users]);
      }
    }
  };

  const refreshSelectedVendor = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BASE_URL}/admin/company`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("access_token")}` },
      });
      const list = res.data.body || [];
      setVendorsList(list);
      const vendor = list.find((v) => String(v.company_id) === String(selectedVendor));
      if (vendor) setSelectedVendorData(vendor);
    } catch (err) {
      console.log(err);
    }
  };

  const handleAddUserType = (e) => {
    const value = e.target.value;
    if (!value || userTypes.includes(value)) return;
    setUserTypes([...userTypes, value]);
  };

  const startNewGroupWizard = () => {
    if (!userTypes.length) {
      toast.error("Add user types under Users first");
      return;
    }
    const allAssigned = configGroups.flatMap((g) => g.user_types);
    const hasUnassigned = userTypes.some((ut) => !allAssigned.includes(ut));
    if (!hasUnassigned) {
      toast.error("All user types are already configured. Edit an existing card to change them.");
      return;
    }
    const g = createConfigGroup();
    setConfigGroups((prev) => [...prev, g]);
    setEditingGroupId(g.id);
    setWizardStep(0);
    setConfigureModalOpen(true);
  };

  // const removeConfigGroup = (groupId) => {
  //   if (configGroups.length <= 1) {
  //     toast.error("At least one configuration group is required");
  //     return;
  //   }
  //   setConfigGroups((prev) => prev.filter((g) => g.id !== groupId));
  //   if (editingGroupId === groupId) {
  //     setConfigureModalOpen(false);
  //     setEditingGroupId(null);
  //   }
  // };

  const openConfigureModal = (groupId) => {
    persistEditingGroup();
    setEditingGroupId(groupId);
    setWizardStep(0);
    setConfigureModalOpen(true);
  };

  const closeConfigureModal = () => {
    persistEditingGroup();
    const draftId = editingGroupId;
    const draft = configGroups.find((g) => g.id === draftId);
    if (
      draft &&
      !draft.user_types.length &&
      !draft.requiredDocs.length &&
      !draft.optionalDocs.length
    ) {
      setConfigGroups((prev) => prev.filter((g) => g.id !== draftId));
    }
    setConfigureModalOpen(false);
    setWizardStep(0);
    setEditingGroupId(null);
  };

  const patchEditingGroup = (patch) => {
    if (!editingGroupId) return;
    setConfigGroups((prev) =>
      prev.map((g) =>
        g.id === editingGroupId ? { ...g, ...patch, matchRulesNested: null } : g,
      ),
    );
  };

  const toggleGroupUserType = (userType) => {
    if (!editingGroup) return;
    const has = editingGroup.user_types.includes(userType);
    const next = has
      ? editingGroup.user_types.filter((u) => u !== userType)
      : [...editingGroup.user_types, userType];
    patchEditingGroup({ user_types: next });
  };

  const setGroupDocMode = (regKey, mode) => {
    if (!editingGroup) return;
    let requiredDocs = editingGroup.requiredDocs.filter((d) => d !== regKey);
    let optionalDocs = editingGroup.optionalDocs.filter((d) => d !== regKey);
    if (mode === DOC_MODE.REQUIRED) requiredDocs.push(regKey);
    if (mode === DOC_MODE.OPTIONAL) optionalDocs.push(regKey);
    patchEditingGroup({ requiredDocs, optionalDocs });
  };


  const handleApiOptionChange = (key, value) => {
    if (!editingGroup) return;
    patchEditingGroup({ apiDetails: { ...editingGroup.apiDetails, [key]: value } });
  };

  const handleMatchRuleChange = (option, is_sub, valueDetails) => {
    if (!editingGroup) return;
    const defaultOptions = matchRulesValidations.find((item) => item?.value === option);
    const prev = editingGroup.matchRules;
    const allOptions = { ...prev };
    if (is_sub) {
      if (allOptions[option]) {
        allOptions[option] = { ...allOptions[option], [valueDetails.key]: valueDetails.value };
      } else {
        allOptions[option] = defaultOptions;
      }
    } else if (allOptions[option]) {
      delete allOptions[option];
    } else {
      allOptions[option] = defaultOptions;
    }
    patchEditingGroup({ matchRules: allOptions });
  };

  const saveVendorKycConfig = async (toastId = "matchRulesUpdate") => {
    if (isSaving) return false;
    if (!selectedVendor || selectedVendor === 0) {
      toast.error("Please select a client");
      return false;
    }

    persistEditingGroup();

  const groupsToSave = configGroups.filter(
      (g) =>
        g.user_types.length > 0 &&
        (g.requiredDocs.length > 0 || g.optionalDocs.length > 0),
    );
    if (!groupsToSave.length) {
      toast.error(
        "Configure at least one KYC setup with user types and required or optional documents",
      );
      return false;
    }

    const userTypesForSave =
      userTypes.length > 0 ? userTypes : selectedVendorData?.user_types || [];

    const kycDynamic = buildKycDynamicOptionsFromGroups(groupsToSave);
    const kycOpts = buildKycOptionsFromGroups(groupsToSave);
    const first = groupsToSave[0];

    toast.loading("Saving...", { id: toastId });
    try {
      await updateVendor({
        id: selectedVendor,
        token: sessionStorage.getItem("access_token"),
        body: {
          industry_id: selectedIndustryId,
          industry_name: selectedIndustryName,
          user_types: JSON.stringify(userTypesForSave),
          kyc_options: JSON.stringify(kycOpts),
          kyc_dynamic_options: JSON.stringify(kycDynamic),
          api_details: JSON.stringify(first.apiDetails),
          match_rules: JSON.stringify(first.matchRules),
        },
      }).unwrap();

      await refreshSelectedVendor();
      return true;
    } catch {
      return false;
    } finally {
      toast.dismiss(toastId);
    }
  };

  const finishConfigureModal = async () => {
    const ok = await saveVendorKycConfig("wizardSave");
    if (!ok) return;
    setConfigureModalOpen(false);
    setWizardStep(0);
    setEditingGroupId(null);
  };

  const goWizardNext = async () => {
    if (!editingGroup) return;
    if (wizardStep === 0 && !editingGroup.user_types.length) {
      toast.error("Select at least one user type");
      return;
    }
    if (
      wizardStep === 1 &&
      !editingGroup.requiredDocs.length &&
      !editingGroup.optionalDocs.length
    ) {
      toast.error("Mark at least one document as Required or Optional");
      return;
    }
    if (wizardStep < WIZARD_STEPS.length - 1) {
      setWizardStep((s) => s + 1);
    } else {
      await finishConfigureModal();
    }
  };

  const goWizardBack = () => {
    if (wizardStep > 0) setWizardStep((s) => s - 1);
  };

  const handleUpdate = () => {
    saveVendorKycConfig("matchRulesUpdate");
  };

  const showContent =
    selectedVendorData && selectedIndustryId != 0 && selectedVendor != 0;

  const visibleConfigGroups = configGroups.filter(
    (g) =>
      g.user_types.length > 0 ||
      g.requiredDocs.length > 0 ||
      g.optionalDocs.length > 0 ||
      (configureModalOpen && g.id === editingGroupId),
  );

  return (
    <>
      <Toaster />
      <div className="page-content w-100">
        <div className="card p-4 w-100">
          <div className="borderbtm">
            <h5 className="card-title mb-0">Match Rules Configuration</h5>
          </div>
          <div className="card-body p-0">
            <div className="row frmds w-100">
              <div className="col-12 col-xl-6 my-2">
                <label>Client</label>
                <select
                  className="js-example-basic-single w-100 rounded-lg shadow-sm shadow-black px-2"
                  value={selectedVendor}
                  onChange={handleVendorChange}
                >
                  <option value={0}>Select Client</option>
                  {vendorsList.map((v) => (
                    <option key={v.company_id} value={v.company_id}>
                      {v.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-12 col-xl-6 my-2">
                <label>Industry</label>
                <select
                  className="js-example-basic-single w-100 rounded-lg shadow-sm shadow-black px-2"
                  value={selectedIndustryId}
                  onChange={handleIndustryChange}
                >
                  <option value={0}>Select Industry</option>
                  {industryList.map((ind) => (
                    <option key={ind.industry_id} value={ind.industry_id}>
                      {ind.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-12 my-2">
                <label>Users</label>
                <select
                  className="border rounded-lg shadow-sm shadow-black px-2 w-100"
                  defaultValue=""
                  onChange={handleAddUserType}
                >
                  <option value="">Select</option>
                  {allAppUserTypeData?.body?.map((x) =>
                    userTypes.includes(x.user_type) ? null : (
                      <option key={x.user_type} value={x.user_type}>
                        {x.user_type}
                      </option>
                    ),
                  )}
                </select>
                {userTypes.map((user) => (
                  <label className="mt-2 mr-2" key={user}>
                    <span className="btn btn-outline-danger d-flex">
                      {user}
                      <GrFormClose
                        className="w-4 h-4 ml-2"
                        onClick={() => {
                          const next = userTypes.filter((u) => u !== user);
                          setUserTypes(next);
                          setConfigGroups((prev) =>
                            prev.map((g) => ({
                              ...g,
                              user_types: g.user_types.filter((u) => u !== user),
                            })),
                          );
                        }}
                      />
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {showContent && userTypes.length > 0 && (
          <div className="card my-4 p-4 w-100">
            <div className="borderbtm mb-3 d-flex justify-content-between align-items-start flex-wrap gap-2">
              <div>
                <h5 className="card-title mb-0">KYC configuration</h5>
              </div>
              <button
                type="button"
                className="btn btn-outline-primary d-flex align-items-center gap-1"
                onClick={startNewGroupWizard}
                title="Add configuration"
              >
                <AiOutlinePlus className="w-5 h-5" />
              </button>
            </div>
            <div className="row g-3 align-items-stretch">
              {visibleConfigGroups.map((group) => {
                const summary = groupSummaryLabel(group);
                const title = groupDisplayTitle(group);
                const isReady =
                  group.user_types.length > 0 &&
                  (group.requiredDocs.length > 0 || group.optionalDocs.length > 0);
                return (
                  <div key={group.id} className="col-12 col-md-6 d-flex">
                    <div className="w-100 h-100 border rounded-lg p-3 bg-white d-flex flex-column">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="font-semibold text-capitalize">{title}</span>
                        {isReady ? (
                          <BsCheckCircleFill className="text-success" />
                        ) : (
                          <BsCircle className="text-muted" />
                        )}
                      </div>
                      <div className="small text-muted flex-grow-1">
                        <div>
                          <strong>Required:</strong> {summary.required}
                        </div>
                        <div>
                          <strong>Optional:</strong> {summary.optional}
                        </div>
                        <div>
                          <strong>Rules:</strong> {summary.rules} enabled
                        </div>
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary mt-3 align-self-start"
                        onClick={() => openConfigureModal(group.id)}
                      >
                        {isReady ? "Edit" : "Configure"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <KycGroupConfigureWizard
          open={configureModalOpen}
          group={editingGroup}
          groupTitle={editingGroup ? groupDisplayTitle(editingGroup) : ""}
          step={wizardStep}
          saving={isSaving}
          poolUserTypes={userTypes}
          disabledUserTypes={typesAssignedElsewhere(editingGroupId)}
          selectedUserTypes={editingGroup?.user_types || []}
          requiredDocs={editingGroup?.requiredDocs || []}
          optionalDocs={editingGroup?.optionalDocs || []}
          selectedApiOptions={editingGroup?.apiDetails || defaultApiOptions}
          matchRules={editingGroup?.matchRules || {}}
          onClose={closeConfigureModal}
          onBack={goWizardBack}
          onNext={goWizardNext}
          onToggleUserType={toggleGroupUserType}
          onSetDocMode={setGroupDocMode}
          onApiChange={handleApiOptionChange}
          onMatchRuleChange={handleMatchRuleChange}
        />

        <div className="card p-4 w-100">
          <div className="d-flex justify-content-center">
            <button
              type="button"
              className="btn btn-outline-primary btnall"
              onClick={handleUpdate}
              disabled={updateDisabled || isSaving}
            >
              {isSaving ? "Saving..." : "Update"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

const UserTypesStep = ({
  poolUserTypes,
  disabledUserTypes,
  selectedUserTypes,
  onToggleUserType,
}) => {
  if (!poolUserTypes?.length) {
    return (
      <p className="text-muted text-center py-3 mb-0">
        Add user types in the <strong>Users</strong> field above, then return here.
      </p>
    );
  }
  return (
    <div className="d-flex w-full gap-2 flex-wrap justify-content-center py-2">
      {poolUserTypes.map((ut) => {
        const selected = selectedUserTypes.includes(ut);
        const disabled = !selected && disabledUserTypes.includes(ut);
        return (
          <label
            key={ut}
            className={`border p-3 rounded-lg ${disabled ? "opacity-50" : "cursor-pointer"} ${selected ? "border-primary bg-light" : ""}`}
            title={
              disabled ? "Already assigned elsewhere — edit that configuration to move it" : ""
            }
          >
            <div
              className="d-flex align-items-center"
              onClick={() => !disabled && onToggleUserType(ut)}
            >
              {selected ? (
                <BsCheckCircleFill className="w-4 mr-2 text-primary" />
              ) : (
                <BsCircle className="w-4 mr-2" />
              )}
              <span className="font-semibold text-capitalize mb-0">{ut}</span>
            </div>
          </label>
        );
      })}
    </div>
  );
};

const KycDocsStep = ({ requiredDocs, optionalDocs, onSetDocMode }) => {
  const modeFor = (key) => {
    if (requiredDocs.includes(key)) return DOC_MODE.REQUIRED;
    if (optionalDocs.includes(key)) return DOC_MODE.OPTIONAL;
    return DOC_MODE.OFF;
  };
  return (
    <div className="py-2">
      <p className="text-muted small text-center mb-3">
        <strong>Required</strong> = must complete for KYC done ·{" "}
        <strong>Optional</strong> = enabled but not required (e.g. bank)
      </p>
      <div className="d-flex flex-column gap-3">
        {REGULATION_KEYS.map(({ key, label }) => {
          const mode = modeFor(key);
          return (
            <div
              key={key}
              className={`border rounded-lg p-3 ${mode !== DOC_MODE.OFF ? "border-primary bg-light" : ""}`}
            >
              <p className="font-semibold mb-2">{label}</p>
              <div className="d-flex gap-2 flex-wrap">
                {[DOC_MODE.OFF, DOC_MODE.REQUIRED, DOC_MODE.OPTIONAL].map((m) => (
                  <button
                    key={m}
                    type="button"
                    className={`btn btn-sm ${mode === m ? "btn-primary" : "btn-outline-secondary"}`}
                    onClick={() => onSetDocMode(key, m)}
                  >
                    {m === DOC_MODE.OFF ? "Off" : m === DOC_MODE.REQUIRED ? "Required" : "Optional"}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// const OverrideOptionsStep = ({
//   useOverrideOptions,
//   overrideDraft,
//   onToggleUseOverride,
//   onOverrideDraftChange,
// }) => {
//   return (
//     <div className="py-2">
//       <p className="text-muted small mb-3">
//         <strong>overrideOptions</strong> merges on top of this group&apos;s config (backend spread).
//       </p>
//       <label className="d-flex align-items-center gap-2 mb-3 cursor-pointer">
//         <input
//           type="checkbox"
//           checked={useOverrideOptions}
//           onChange={(e) => onToggleUseOverride(e.target.checked)}
//         />
//         <span className="font-semibold">Enable override for this group</span>
//       </label>
//       {useOverrideOptions && (
//         <textarea
//           className="form-control font-monospace small"
//           rows={8}
//           value={overrideDraft}
//           onChange={(e) => onOverrideDraftChange(e.target.value)}
//           placeholder='{"optional":["bank"]}'
//         />
//       )}
//     </div>
//   );
// };

const ApiOptionsStep = ({ requiredDocs, optionalDocs, selectedApiOptions, onApiChange }) => {
  const activeKeys = REGULATION_KEYS.map((r) => r.key).filter(
    (k) => requiredDocs.includes(k) || optionalDocs.includes(k),
  );
  return (
  <div className="d-flex w-full gap-3 flex-wrap justify-content-center py-2">
    {kycVerificationOptions
      .filter((item) => activeKeys.includes(item.value))
      .map((item) => {
      const { name, value, options } = item;
      return (
        <div key={value} className="border p-3 rounded-lg min-w-[180px]">
          <p className="font-semibold mb-2">{name}</p>
          <select
            value={selectedApiOptions[value]}
            onChange={(e) => onApiChange(value, e.target.value)}
            className="w-full form-select"
          >
            {options.map((opt) => (
              <option value={opt} key={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>
      );
    })}
  </div>
  );
};

const MatchRulesStep = ({ matchRules, onMatchRuleChange }) => (
  <div className="grid grid-cols-2 md:grid-cols-3 w-full gap-2">
    {matchRulesValidations.map((rule, index) => {
      const { pause, name, value, deny, threshold, with_mobile } = rule;
      const is_rule_enabled = matchRules[value];

      if (pause) {
        return (
          <div key={`pause-${pause}`} className="w-full col-span-full pt-2 pb-1">
            <h6 className="mb-0 px-2 py-1 bg-dark text-white rounded d-inline-block">
              {pause} rules
            </h6>
          </div>
        );
      }

      return (
        <label
          key={index}
          className={`rounded-lg border overflow-hidden ${is_rule_enabled ? "border-success" : ""}`}
        >
          <div
            className={`px-2 py-2 d-flex align-items-center cursor-pointer ${is_rule_enabled ? "bg-success bg-opacity-25" : ""}`}
            onClick={() => onMatchRuleChange(value, false, null)}
          >
            {is_rule_enabled ? (
              <BsCheckCircleFill className="w-3 mr-2 flex-shrink-0" />
            ) : (
              <BsCircle className="w-3 mr-2 flex-shrink-0" />
            )}
            <span className="small font-semibold mb-0">{name}</span>
          </div>
          {!!is_rule_enabled && (
            <div className="px-2 pb-2 small">
              {typeof deny !== "undefined" && (
                <div
                  className="cursor-pointer py-1"
                  onClick={() =>
                    onMatchRuleChange(value, true, {
                      key: "deny",
                      value: !is_rule_enabled?.deny,
                    })
                  }
                >
                  Deny {is_rule_enabled?.deny ? "✓" : "○"}
                </div>
              )}
              {typeof threshold !== "undefined" && (
                <div className="d-flex align-items-center gap-1">
                  <span>Threshold</span>
                  <input
                    type="number"
                    max={100}
                    min={0}
                    className="form-control form-control-sm"
                    style={{ width: 56 }}
                    value={is_rule_enabled?.threshold}
                    onChange={(e) =>
                      onMatchRuleChange(value, true, {
                        key: "threshold",
                        value: Number(e.target.value),
                      })
                    }
                  />
                </div>
              )}
              {typeof with_mobile !== "undefined" && (
                <select
                  className="form-select form-select-sm mt-1"
                  value={is_rule_enabled?.with_mobile ? "true" : "false"}
                  onChange={(e) =>
                    onMatchRuleChange(value, true, {
                      key: "with_mobile",
                      value: e.target.value === "true",
                    })
                  }
                >
                  <option value="true">Mobile</option>
                  <option value="false">Profile name</option>
                </select>
              )}
            </div>
          )}
        </label>
      );
    })}
  </div>
);

const KycGroupConfigureWizard = ({
  open,
  group,
  groupTitle,
  step,
  saving,
  poolUserTypes,
  disabledUserTypes,
  selectedUserTypes,
  requiredDocs,
  optionalDocs,
  selectedApiOptions,
  matchRules,
  onClose,
  onBack,
  onNext,
  onToggleUserType,
  onSetDocMode,
  onApiChange,
  onMatchRuleChange,
}) => {
  if (!group) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle className="border-bottom text-capitalize">
        {groupTitle || "Setup"} — configuration
      </DialogTitle>
      <DialogContent dividers sx={{ minHeight: 320 }}>
        <Stepper activeStep={step} alternativeLabel className="mb-4">
          {WIZARD_STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {step === 0 && (
          <>
            <p className="text-muted small text-center mb-3">
              Select user types for this configuration (e.g. retailer + distributor together).
              Greyed types are already used in another configuration.
            </p>
            <UserTypesStep
              poolUserTypes={poolUserTypes}
              disabledUserTypes={disabledUserTypes}
              selectedUserTypes={selectedUserTypes}
              onToggleUserType={onToggleUserType}
            />
          </>
        )}

        {step === 1 && (
          <>
            <p className="text-muted small text-center mb-3">
              KYC documents for: <strong>{selectedUserTypes.join(", ") || "—"}</strong>
            </p>
            <KycDocsStep
              requiredDocs={requiredDocs}
              optionalDocs={optionalDocs}
              onSetDocMode={onSetDocMode}
            />
          </>
        )}

        {step === 2 && (
          <>
            <p className="text-muted small text-center mb-3">
              API type for enabled documents (Aadhaar / PAN / GSTIN / UPI / Bank)
            </p>
            <ApiOptionsStep
              requiredDocs={requiredDocs}
              optionalDocs={optionalDocs}
              selectedApiOptions={selectedApiOptions}
              onApiChange={onApiChange}
            />
          </>
        )}

        {step === 3 && (
          <>
            <p className="text-muted small text-center mb-3">
              Match rules for this configuration
            </p>
            <div style={{ maxHeight: "50vh", overflowY: "auto" }}>
              <MatchRulesStep
                matchRules={matchRules}
                onMatchRuleChange={onMatchRuleChange}
              />
            </div>
          </>
        )}

      </DialogContent>
      <DialogActions className="px-3 pb-3">
        <Button onClick={onClose} color="inherit" disabled={saving}>
          Cancel
        </Button>
        {step > 0 && (
          <Button onClick={onBack} variant="outlined" disabled={saving}>
            Back
          </Button>
        )}
        <Button onClick={onNext} variant="contained" color="primary" disabled={saving}>
          {saving
            ? "Saving..."
            : step === WIZARD_STEPS.length - 1
              ? "Save"
              : "Next"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MatchRulesConfiguration;
